import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { EmailTemplateEdit } from '../model/email-template';
import { TabStripComponent, SelectEvent } from '@progress/kendo-angular-layout';
import insertTextAtCursor from 'insert-text-at-cursor';
import { TranslateService } from '@ngx-translate/core';
import { SettingService } from 'src/app/core/service/setting.service';
import { EmailTemplateService } from 'src/app/core/service/email-template.service';
import { UserService } from 'src/app/core/service/user.service';
import { sortBy } from 'lodash';
import { Base64 } from 'js-base64';

@Component({
  standalone: false,
  selector: 'app-email-template-edit',
  templateUrl: './email-template-edit.component.html',
  styleUrls: ['./email-template-edit.component.scss']
})
export class EmailTemplateEditComponent implements OnInit
{
  @ViewChild('tabstrip') public tabstrip: TabStripComponent;
  @ViewChild('templateContent') public templateContent: ElementRef;

  @Input() public isNew = false;

  @Output() public cancelling: EventEmitter<any> = new EventEmitter();
  @Output() public save: EventEmitter<EmailTemplateEdit> = new EventEmitter();

  public templateMimeTypes: Array<{ text: string; value: string }>;

  // Registration and Forgot Password tags - EMAIL-TEMPLATE-TAGS-REGISTRATION
  public registrationTags: Array<{ text: string; value: string; click: any }> = [
    { text: 'Expiry Time', value: '${expiryTime}', click: null },
    { text: 'Url', value: '${url}', click: null },
    { text: 'User Name', value: '${name}', click: null },
    { text: 'Application Name', value: '${applicationName}', click: null },
  ]; 
  
  // Forgot Username tags - EMAIL-TEMPLATE-TAGS-FORGOT-USERNAME
  public forgotUsernameTags: Array<{ text: string; value: string; click: any }> = [
    { text: 'User Name', value: '${name}', click: null },
    { text: 'Username', value: '${username}', click: null },
    { text: 'Application Name', value: '${applicationName}', click: null },
  ];

  // Appointment Schedule, Cancelled and confirmation tags - EMAIL-TEMPLATE-TAGS-APPOINTMENT
  public appointmentTags: Array<{ text: string; value: string; click: any }> = [
    { text: 'Appointment Date', value: '${appointmentDate}', click: null },
    { text: 'Appointment Time', value: '${appointmentTime}', click: null },
    { text: 'Coordinator Name', value: '${coordinatorName}', click: null },
    { text: 'Coordinator Email', value: '${coordinatorEmail}', click: null },
    { text: 'Coordinator Phone', value: '${coordinatorPhoneNumber}', click: null },
    { text: 'Organization Name', value: '${organizationName}', click: null },
    { text: 'Organization Contact Info', value: '${organizationContactInfo}', click: null },
    { text: 'Provider Name', value: '${providerName}', click: null },
    { text: 'Provider Address', value: '${providerAddress}', click: null },
    { text: 'Url', value: '${url}', click: null },
    { text: 'Application Name', value: '${applicationName}', click: null },
  ];

  // SMS appointment confirmation tags - SMS-TEMPLATE-TAGS-APPOINTMENT-CONFIRMATION
  public smsAppointmentConfirmationTags: Array<{ text: string; value: string; click: any }> = [
    { text: 'Appointment Date', value: '${appointmentDate}', click: null },
    { text: 'Appointment Time', value: '${appointmentTime}', click: null },
    { text: 'Coordinator Name', value: '${coordinatorName}', click: null },
    { text: 'Coordinator Email', value: '${coordinatorEmail}', click: null },
    { text: 'Coordinator Phone', value: '${coordinatorPhoneNumber}', click: null },
    { text: 'Organization Name', value: '${organizationName}', click: null },
    { text: 'Organization Contact Info', value: '${organizationContactInfo}', click: null },
    { text: 'Provider Name', value: '${providerName}', click: null },
    { text: 'Provider Address', value: '${providerAddress}', click: null },
    { text: 'Url', value: '${url}', click: null },
    { text: 'Application Name', value: '${applicationName}', click: null },
  ];

  // Appointment Reschedule tags - EMAIL-TEMPLATE-TAGS-APPOINTMENT-RESCHEDULE
  public appointmentRescheduleTags: Array<{ text: string; value: string; click: any }> = [
    { text: 'Appointment Date', value: '${appointmentDate}', click: null },
    { text: 'Previous Appointment Date', value: '${oldAppointmentDate}', click: null },
    { text: 'Appointment Time', value: '${appointmentTime}', click: null },
    { text: 'Previous Appointment Time', value: '${oldAppointmentTime}', click: null },
    { text: 'Coordinator Name', value: '${coordinatorName}', click: null },
    { text: 'Coordinator Email', value: '${coordinatorEmail}', click: null },
    { text: 'Coordinator Phone', value: '${coordinatorPhoneNumber}', click: null },
    { text: 'Organization Name', value: '${organizationName}', click: null },
    { text: 'Organization Contact Info', value: '${organizationContactInfo}', click: null },
    { text: 'Provider Name', value: '${providerName}', click: null },
    { text: 'Previous Provider Name', value: '${oldProviderName}', click: null },
    { text: 'Provider Address', value: '${providerAddress}', click: null },
    { text: 'Application Name', value: '${applicationName}', click: null },
  ];

  // Consent confirmation tags - EMAIL-TEMPLATE-TAGS-CONSENT-CONFIRMATION
  public consentConfirmationTags: Array<{ text: string; value: string; click: any }> = [
    { text: 'Url', value: '${url}', click: null },
    { text: 'Organization Name', value: '${organizationName}', click: null },
    { text: 'Organization Contact Info', value: '${organizationContactInfo}', click: null },
    { text: 'Expiry Time', value: '${expiryTime}', click: null },
    { text: 'Application Name', value: '${applicationName}', click: null },
  ];

  // Consent confirmation tags - SMS-TEMPLATE-TAGS-CONSENT-CONFIRMATION
  public smsConsentConfirmationTags: Array<{ text: string; value: string; click: any }> = [
    { text: 'Url', value: '${url}', click: null },
    { text: 'Organization Name', value: '${organizationName}', click: null },
    { text: 'Organization Contact Info', value: '${organizationContactInfo}', click: null },
    { text: 'Expiry Time', value: '${expiryTime}', click: null },
    { text: 'Application Name', value: '${applicationName}', click: null },
  ];

  // Community Registration tags - EMAIL-TEMPLATE-TAGS-COMMUNITY-REGISTRATION
  public communityRegistrationTags: Array<{ text: string; value: string; click: any }> = [
    { text: 'Name', value: '${name}', click: null },
    { text: 'Expiry Time', value: '${expiryTime}', click: null },
    { text: 'Url', value: '${url}', click: null },
    { text: 'Application Name', value: '${applicationName}', click: null },
  ];

  // Community Password Recovery tags - EMAIL-TEMPLATE-TAGS-COMMUNITY-PASSWORD-RECOVERY
  public communityPasswordRecoveryTags: Array<{ text: string; value: string; click: any }> = [
    { text: 'Expiry Time', value: '${expiryTime}', click: null },
    { text: 'Url', value: '${url}', click: null },
    { text: 'Application Name', value: '${applicationName}', click: null },
  ];

  // Community Username Recovery tags - EMAIL-TEMPLATE-TAGS-COMMUNITY-USERNAME-RECOVERY
  public communityUsernameRecoveryTags: Array<{ text: string; value: string; click: any }> = [
    { text: 'Username', value: '${username}', click: null },
    { text: 'Application Name', value: '${applicationName}', click: null },
  ];

  // Message Received tags - EMAIL-TEMPLATE-TAGS-MESSAGE-RECEIVED
  public messageReceivedTags: Array<{ text: string; value: string; click: any }> = [
    { text: 'From', value: '${from}', click: null },
    { text: 'Url', value: '${url}', click: null },
    { text: 'Organization Name', value: '${organizationName}', click: null },
    { text: 'Organization Contact Info', value: '${organizationContactInfo}', click: null },
    { text: 'Application Name', value: '${applicationName}', click: null },
  ];

  // Provider referral tags - EMAIL-TEMPLATE-TAGS-PROVIDER-REFERRAL
  public providerReferralTags: Array<{ text: string; value: string; click: any }> = [
    { text: 'Patient Name', value: '${patientName}', click: null },
    { text: 'Coordinator Name', value: '${coordinatorName}', click: null },
    { text: 'Coordinator Phone Number', value: '${coordinatorPhoneNumber}', click: null },
    { text: 'Coordinator Email', value: '${coordinatorEmail}', click: null },
    { text: 'Organization Name', value: '${organizationName}', click: null },
    { text: 'Organization Contact Info', value: '${organizationContactInfo}', click: null },
    { text: 'Channel Name', value: '${channelName}', click: null },
    { text: 'Service Name', value: '${communityProgramName}', click: null },
    { text: 'Url', value: '${url}', click: null },
    { text: 'Application Name', value: '${applicationName}', click: null },
  ];

  // Referral tags - EMAIL-TEMPLATE-TAGS-REFERRAL
  public referralTags: Array<{ text: string; value: string; click: any }> = [
    { text: 'Patient Name', value: '${patientName}', click: null },
    { text: 'Coordinator Name', value: '${coordinatorName}', click: null },
    { text: 'Coordinator Phone Number', value: '${coordinatorPhoneNumber}', click: null },
    { text: 'Coordinator Email', value: '${coordinatorEmail}', click: null },
    { text: 'Organization Name', value: '${organizationName}', click: null },
    { text: 'Organization Contact Info', value: '${organizationContactInfo}', click: null },
    { text: 'Service Name', value: '${communityProgramName}', click: null },
    { text: 'Service Category', value: '${serviceCategory}', click: null },
    { text: 'Service Type', value: '${serviceType}', click: null },
    { text: 'Service Address', value: '${serviceAddress}', click: null },
    { text: 'Program Contact Name', value: '${programContactName}', click: null },
    { text: 'Program Contact Phone', value: '${programContactPhone}', click: null },
    { text: 'Program Contact Email', value: '${programContactEmail}', click: null },
    { text: 'Program Contact Url', value: '${programContactUrl}', click: null },
    { text: 'Referral Contact Name', value: '${referralContactName}', click: null },
    { text: 'Referral Contact Phone', value: '${referralContactPhone}', click: null },
    { text: 'Referral Contact Email', value: '${referralContactEmail}', click: null },
    { text: 'Referral Contact Url', value: '${referralContactUrl}', click: null },
    { text: 'Application Name', value: '${applicationName}', click: null }, 
  ];  
  
  // Send Community Invite tags - EMAIL-TEMPLATE-TAGS-SEND-COMMUNITY-INVITE
  public communityInviteTags: Array<{ text: string; value: string; click: any }> = [
    { text: 'Expiry Time', value: '${expiryTime}', click: null },
    { text: 'Url', value: '${url}', click: null },
    { text: 'Patient Name', value: '${patientName}', click: null },
    { text: 'Coordinator Name', value: '${coordinatorName}', click: null },
    { text: 'Coordinator Phone Number', value: '${coordinatorPhoneNumber}', click: null },
    { text: 'Coordinator Email', value: '${coordinatorEmail}', click: null },
    { text: 'Organization Name', value: '${organizationName}', click: null },
    { text: 'Application Name', value: '${applicationName}', click: null },
  ];

  // Invite tags - SMS-TEMPLATE-TAGS-COMMUNITY-INVITE
  public smsCommunityInviteTags: Array<{ text: string; value: string; click: any }> = [
    { text: 'Expiry Time', value: '${expiryTime}', click: null },
    { text: 'Url', value: '${url}', click: null },
    { text: 'Patient Name', value: '${patientName}', click: null },
    { text: 'Coordinator Name', value: '${coordinatorName}', click: null },
    { text: 'Coordinator Phone Number', value: '${coordinatorPhoneNumber}', click: null },
    { text: 'Coordinator Email', value: '${coordinatorEmail}', click: null },
    { text: 'Organization Name', value: '${organizationName}', click: null },
    { text: 'Application Name', value: '${applicationName}', click: null },
  ];

  // Updated notification tag - EMAIL-TEMPLATE-TAGS-PASSWORD-UPDATED
  public updatedNotificationTags: Array<{ text: string; value: string; click: any }> = [
    { text: 'User Name', value: '${name}', click: null },
    { text: 'Application Name', value: '${applicationName}', click: null },
  ];

  // Community Service Correction tags - EMAIL-TEMPLATE-TAGS-COMMUNITY-SERVICE-CORRECTION
  public communityServiceCorrectionTags: Array<{ text: string; value: string; click: any }> = [
    { text: 'Contact Name', value: '${contactName}', click: null },
    { text: 'Reporter Name', value: '${reporterName}', click: null },
    { text: 'Reporter Organization', value: '${reporterOrganization}', click: null },
    { text: 'Correction Type', value: '${correctionType}', click: null },
    { text: 'Description', value: '${description}', click: null },
    { text: 'Reporter Email', value: '${reporterEmail}', click: null },
    { text: 'Service Name', value: '${serviceName}', click: null },
    { text: 'Service Address', value: '${serviceAddress}', click: null },
    { text: 'Application Name', value: '${applicationName}', click: null },
  ];

  // Community Service Batch Corrections tags - EMAIL-TEMPLATE-TAGS-COMMUNITY-SERVICE-BATCH-CORRECTIONS
  public communityServiceBatchCorrectionsTags: Array<{ text: string; value: string; click: any }> = [
    { text: 'Contact Name', value: '${contactName}', click: null },
    { text: 'Reporter Name', value: '${reporterName}', click: null },
    { text: 'Reporter Organization', value: '${reporterOrganization}', click: null },
    { text: 'Correction Type', value: '${correctionType}', click: null },
    { text: 'Description', value: '${description}', click: null },
    { text: 'Reporter Email', value: '${reporterEmail}', click: null },
    { text: 'Service Name', value: '${serviceName}', click: null },
    { text: 'Service Address', value: '${serviceAddress}', click: null },
    { text: 'Loop Start', value: '${loopStart}', click: null },
    { text: 'Loop End', value: '${loopEnd}', click: null },
    { text: 'Application Name', value: '${applicationName}', click: null },
  ];

  // Image Capture Link tags - EMAIL-TEMPLATE-TAGS-IMAGE-CAPTURE-LINK
  public imageCaptureLinkTags: Array<{ text: string; value: string; click: any }> = [
    { text: 'Expiry Time', value: '${expiryTime}', click: null },
    { text: 'Url', value: '${url}', click: null },
    { text: 'Application Name', value: '${applicationName}', click: null },
  ];

  // Consent Request Link tags - EMAIL-TEMPLATE-TAGS-CONSENT-REQUEST-LINK
  public consentRequestLinkTags: Array<{ text: string; value: string; click: any }> = [
    { text: 'Expiry Time', value: '${expiryTime}', click: null },
    { text: 'Url', value: '${url}', click: null },
    { text: 'Application Name', value: '${applicationName}', click: null },
  ];

  // Two Step Verification tags - EMAIL-TEMPLATE-TAGS-TWO-STEP-VERIFICATION
  public twoStepVerifcationTags: Array<{ text: string; value: string; click: any }> = [
    { text: 'Expiry Time', value: '${expiryTime}', click: null },
    { text: 'Code', value: '${code}', click: null },
    { text: 'Application Name', value: '${applicationName}', click: null },
  ];

  // Opt Out tags - EMAIL-TEMPLATE-TAGS-OPT-OUT
  public optOutTags: Array<{ text: string; value: string; click: any }> = [
    { text: 'User Name', value: '${name}', click: null },
    { text: 'Opt Out Date', value: '${optOutDate}', click: null },
    { text: 'Opt Out Reason', value: '${optOutReason}', click: null },
    { text: 'Application Name', value: '${applicationName}', click: null },
  ];  

  // Font sizes for the email content type
  public contentFontSizes: Array<{ style: string; class: string }> = [
    { style: 'width:30px;height:30px;', class: 'content-small' },
    { style: 'width:36px;height:36px;', class: 'content-medium' },
    { style: 'width:43px;height:43px;', class: 'content-large' },
  ];

  public selectedFontSize: any;
  public editForm: any;
  public editFormActive = false;
  public tags: Array<{ text: string; value: string; click: any }> = [];
  public typeCode: string;
  public languages: Array<{ text: string; value: string }>;

  private emailTemplateEdit: EmailTemplateEdit;

  constructor(
    private fb: UntypedFormBuilder,
    private userService: UserService,
    private settingService: SettingService,
    private emailTemplateService: EmailTemplateService,
    private translate: TranslateService)
  { }

  public get content(): UntypedFormControl
  {
    return this.editForm.get('content');
  }

  public get language(): UntypedFormControl
  {
    return this.editForm.get('language');
  }

  public get name(): UntypedFormControl
  {
    return this.editForm.get('name');
  }

  public get mimeType(): UntypedFormControl
  {
    return this.editForm.get('mimeType');
  }

  @Input() public set model(emailTemplate: EmailTemplateEdit)
  {
    this.initModel(emailTemplate);
  }

  public ngOnInit(): void
  {
    this.templateMimeTypes = this.emailTemplateService.templateMimeTypes;
    this.languages = this.userService.preferredLanguages;
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        // WARNING: Make sure the order of the comman delimite resources matches the tags' array
        const tagsResource = [];
        tagsResource.push({ tags: this.registrationTags, translationKey: 'EMAIL-TEMPLATE-TAGS-REGISTRATION' });
        tagsResource.push({ tags: this.forgotUsernameTags, translationKey: 'EMAIL-TEMPLATE-TAGS-FORGOT-USERNAME' });
        tagsResource.push({ tags: this.appointmentTags, translationKey: 'EMAIL-TEMPLATE-TAGS-APPOINTMENT' });
        tagsResource.push({ tags: this.appointmentRescheduleTags, translationKey: 'EMAIL-TEMPLATE-TAGS-APPOINTMENT-RESCHEDULE' });
        tagsResource.push({ tags: this.consentConfirmationTags, translationKey: 'EMAIL-TEMPLATE-TAGS-CONSENT-CONFIRMATION' });
        tagsResource.push({ tags: this.communityRegistrationTags, translationKey: 'EMAIL-TEMPLATE-TAGS-COMMUNITY-REGISTRATION' });
        tagsResource.push({ tags: this.communityPasswordRecoveryTags, translationKey: 'EMAIL-TEMPLATE-TAGS-COMMUNITY-PASSWORD-RECOVERY' });
        tagsResource.push({ tags: this.communityUsernameRecoveryTags, translationKey: 'EMAIL-TEMPLATE-TAGS-COMMUNITY-USERNAME-RECOVERY' });
        tagsResource.push({ tags: this.messageReceivedTags, translationKey: 'EMAIL-TEMPLATE-TAGS-MESSAGE-RECEIVED' });
        tagsResource.push({ tags: this.providerReferralTags, translationKey: 'EMAIL-TEMPLATE-TAGS-PROVIDER-REFERRAL' });
        tagsResource.push({ tags: this.communityInviteTags, translationKey: 'EMAIL-TEMPLATE-TAGS-SEND-COMMUNITY-INVITE' });
        tagsResource.push({ tags: this.referralTags, translationKey: 'EMAIL-TEMPLATE-TAGS-REFERRAL' });
        tagsResource.push({ tags: this.smsAppointmentConfirmationTags, translationKey: 'SMS-TEMPLATE-TAGS-APPOINTMENT-CONFIRMATION' });
        tagsResource.push({ tags: this.smsConsentConfirmationTags, translationKey: 'SMS-TEMPLATE-TAGS-CONSENT-CONFIRMATION' });
        tagsResource.push({ tags: this.smsCommunityInviteTags, translationKey: 'SMS-TEMPLATE-TAGS-COMMUNITY-INVITE' });
        tagsResource.push({ tags: this.updatedNotificationTags, translationKey: 'EMAIL-TEMPLATE-TAGS-PASSWORD-UPDATED' });
        tagsResource.push({
          tags: this.communityServiceCorrectionTags,
          translationKey: 'EMAIL-TEMPLATE-TAGS-COMMUNITY-SERVICE-CORRECTION'
        });
        tagsResource.push({
          tags: this.communityServiceBatchCorrectionsTags,
          translationKey: 'EMAIL-TEMPLATE-TAGS-COMMUNITY-SERVICE-BATCH-CORRECTIONS'
        });
        tagsResource.push({
          tags: this.imageCaptureLinkTags,
          translationKey: 'EMAIL-TEMPLATE-TAGS-IMAGE-CAPTURE-LINK'
        });
        tagsResource.push({
          tags: this.twoStepVerifcationTags,
          translationKey: 'EMAIL-TEMPLATE-TAGS-TWO-STEP-VERIFICATION'
        });
        tagsResource.push({
          tags: this.consentRequestLinkTags,
          translationKey: 'EMAIL-TEMPLATE-TAGS-CONSENT-REQUEST-LINK'
        });
        tagsResource.push({ 
          tags: this.optOutTags, 
          translationKey: 'EMAIL-TEMPLATE-TAGS-OPT-OUT' 
        });

        this.translate.get(tagsResource.map(a => a.translationKey)).subscribe({
          next: (res: string) =>
          {
            tagsResource.forEach(resource =>
            {
              this.setTagsTexts(resource.tags, resource.translationKey, res, sessionSetting.patientLabel);
            });
          }
        });
      }
    });
  }

  public onSave(e: Event): void
  {
    e.preventDefault();

    const objectToPersist = this.emailTemplateEdit;
    objectToPersist.content = this.content.value;
    objectToPersist.name = this.name.value;
    objectToPersist.language = this.language.value;
    objectToPersist.mimeTypeCode = this.mimeType.value;

    this.save.emit(objectToPersist);
    this.editFormActive = false;
  }

  public onCancel(e: Event): void
  {
    e.preventDefault();
    this.closeEditForm();
  }

  public onTabSelect = (e: SelectEvent): void =>
  {
    if (e.index === 0)
    {
      this.setEmailTemplateFocus();
    }
  };

  public setSelectFontSite(fontSize: any): void
  {
    this.selectedFontSize = fontSize;
  }

  // Allow Add/Update only for Community Invite
  public isAllowAddUpdate(): boolean
  {
    return (this.emailTemplateEdit.typeCode === 'CommunityInvite' && this.emailTemplateEdit.createdBy !== 'SYSTEM');
  }

  /**
   * This method set the translation texts for the tags array.
   *
   * @param tags Tags
   * @param translationKey Translation key to use for the tags list
   * @param res Translation resources
   */
  private setTagsTexts(tags: any, translationKey: string, res: any, patientLabel: string): void
  {
    const splitted = res[translationKey].split(',');
    for (let i = 0; i < splitted.length; i++)
    {
      if (tags.length > i)
      {
        if (splitted[i].includes('{{patientLabel}}'))
        {
          tags[i].text = splitted[i].replace('{{patientLabel}}', patientLabel);
        }
        else
        {
          tags[i].text = splitted[i];
        }
      }
    }
  }

  private closeEditForm(): void
  {
    this.editFormActive = false;
    this.cancelling.emit();
  }

  private initModel(emailTemplate: EmailTemplateEdit)
  {
    if (emailTemplate === undefined)
    {
      return;
    }

    this.typeCode = emailTemplate.typeCode;
    this.setSelectFontSite(this.contentFontSizes[1]);
    this.emailTemplateEdit = emailTemplate;

    // Set proper tags to use based on the email template type
    switch (emailTemplate.typeCode.toUpperCase())
    {
      case 'REGISTRATION':
      case 'RESETPASSWORD':
        this.tags = sortBy(this.registrationTags, ['text']);
        break;
      case 'FORGOTUSERNAME':
        this.tags = sortBy(this.forgotUsernameTags, ['text']);
        break;
      case 'APPOINTMENTSCHEDULED':
      case 'APPOINTMENTCANCELLED':
      case 'APPOINTMENTCONFIRMATION':
        this.tags = sortBy(this.appointmentTags, ['text']);
        break;
      case 'APPOINTMENTRESCHEDULED':
        this.tags = sortBy(this.appointmentRescheduleTags, ['text']);
        break;
      case 'COMMUNITYSERVICECORRECTION':
        this.tags = sortBy(this.communityServiceCorrectionTags, ['text']);
        break;
      case 'COMMUNITYSERVICEBATCHCORRECTIONS':
        this.tags = sortBy(this.communityServiceBatchCorrectionsTags, ['text']);
        break;
      case 'CONSENTCONFIRMATION':
        this.tags = sortBy(this.consentConfirmationTags, ['text']);
        break;
      case 'COMMUNITYREGISTRATION':
        this.tags = sortBy(this.communityRegistrationTags, ['text']);
        break;
      case 'COMMUNITYPASSWORDRECOVERY':
        this.tags = sortBy(this.communityPasswordRecoveryTags, ['text']);
        break;
      case 'COMMUNITYUSERNAMERECOVERY':
        this.tags = sortBy(this.communityUsernameRecoveryTags, ['text']);
        break;
      case 'MESSAGERECEIVED':
        this.tags = sortBy(this.messageReceivedTags, ['text']);
        break;
      case 'PROVIDERREFERRAL':
        this.tags = sortBy(this.providerReferralTags, ['text']);
        break;
      case 'COMMUNITYINVITE':
        this.tags = sortBy(this.communityInviteTags, ['text']);
        break;
      case 'MEMBERREFERRAL':
      case 'USERREFERRAL':
      case 'SMSMEMBERREFERRAL':
      case 'SMSUSERREFERRAL':
        this.tags = sortBy(this.referralTags, ['text']);
        break;
      case 'SMSAPPOINTMENTCONFIRMATION':
        this.tags = sortBy(this.smsAppointmentConfirmationTags, ['text']);
        break;
      case 'SMSCONSENTCONFIRMATION':
        this.tags = sortBy(this.smsConsentConfirmationTags, ['text']);
        break;
      case 'SMSCOMMUNITYINVITE':
        this.tags = sortBy(this.smsCommunityInviteTags, ['text']);
        break;
      case 'PASSWORDUPDATED':
      case 'COMMUNITYPASSWORDUPDATED':
        this.tags = sortBy(this.updatedNotificationTags, ['text']);
        break;
      case 'IMAGECAPTURELINK':
      case 'SMSIMAGECAPTURELINK':
        this.tags = sortBy(this.imageCaptureLinkTags, ['text']);
        break;
      case 'CONSENTREQUESTLINK':
      case 'SMSCONSENTREQUESTLINK':
        this.tags = sortBy(this.consentRequestLinkTags, ['text']);
        break;
      case 'AUTHTWOSTEP':
      case 'SMSAUTHTWOSTEP':
        this.tags = sortBy(this.twoStepVerifcationTags, ['text']);
        break;
      case 'OPTOUT':
          this.tags = sortBy(this.optOutTags, ['text']);
          break;
    }

    // Set click event on all tags options
    this.tags.forEach(t =>
    {
      t.click = (dataItem) =>
      {
        const el = this.templateContent.nativeElement;
        insertTextAtCursor(el, dataItem.value);
      };
    });

    // Set form
    this.editForm = this.fb.group({
      id: [emailTemplate.id],
      name: [{ value: emailTemplate.name, disabled: false }, Validators.required],
      type: { value: emailTemplate.type, disabled: true },
      mimeType: [{ value: emailTemplate.mimeType, disabled: !this.isAllowAddUpdate() }, Validators.required],
      mimeTypeCode: { value: emailTemplate.mimeTypeCode, disabled: true },
      language: [{ value: emailTemplate.language, disabled: !this.isAllowAddUpdate() }, Validators.required],
      content: [{ value: '', disabled: false }, Validators.required],
    });
    this.content.setValue(Base64.decode(emailTemplate.content));
    this.editFormActive = true;
  }

  private setEmailTemplateFocus(): void
  {
    setTimeout(() =>
    {
      this.templateContent.nativeElement.focus();
    });
  }
}
